import { ApiService } from "../utilities";
import cookieStorage from "../utilities/cookie_storage";


const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Utility to convert VAPID public key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const NotificationService = {
  /**
   * Check if the browser supports push notifications
   */
  isSupported: () => {
    return "serviceWorker" in navigator && "PushManager" in window;
  },

  /**
   * Get the current permission status
   */
  getPermissionStatus: () => {
    return Notification.permission;
  },

  /**
   * Validate VAPID Public Key
   */
  isValidVapidKey: () => {
    if (!VAPID_PUBLIC_KEY) {
      console.error("VAPID_PUBLIC_KEY is undefined. Did you restart your dev server?");
      return false;
    }
    if (VAPID_PUBLIC_KEY === "CHANGE_ME_TO_YOUR_PUBLIC_VAPID_KEY") {
      console.error("VAPID_PUBLIC_KEY is still the placeholder. Please update your .env file.");
      return false;
    }
    return true;
  },

  /**
   * Register the service worker
   */
  registerServiceWorker: async () => {
    if (!NotificationService.isSupported()) {
      console.warn("Push notifications are not supported in this browser.");
      return null;
    }

    try {
      console.log("Registering Service Worker...");
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });
      console.log("Service Worker registered with scope:", registration.scope);
      return registration;
    } catch (err) {
      console.error("Service Worker registration failed:", err);
      return null;
    }
  },

  /**
   * Subscribe the user to push notifications
   */
  subscribeUser: async (userId) => {
    if (!NotificationService.isSupported()) return null;

    console.log("Starting subscribeUser. VAPID Key status:", NotificationService.isValidVapidKey() ? "Valid" : "Invalid");

    if (!NotificationService.isValidVapidKey()) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;

    try {
      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log("Existing subscription found:", subscription.endpoint);
      } else {
        console.log("No existing subscription. Requesting permission...");
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.warn("Permission for notifications was denied.");
          return null;
        }

        console.log("Permission granted. Subscribing to Push Manager...");
        const subscribeOptions = {
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        };

        subscription = await registration.pushManager.subscribe(subscribeOptions);
        console.log("New subscription created successfully:", subscription);
      }

      // Sync subscription with backend
      const activeUserId = userId || cookieStorage.getItem("userId");
      if (activeUserId && subscription) {
        console.log("Syncing subscription with backend for userId:", activeUserId);
        await ApiService.subscribePush({
          userId: activeUserId,
          subscription: subscription.toJSON(),
        });
        console.log("Subscription synced with backend successfully.");
      } else {
        console.warn("Subscription created but userId is missing. Backend sync skipped.");
      }

      return subscription;
    } catch (err) {
      console.error("Failed to subscribe the user: ", err);
      if (err.name === 'InvalidCharacterError' || err.name === 'TypeError') {
        console.error("VAPID key formatting error. Ensure REACT_APP_VAPID_PUBLIC_KEY is a correct Base64 string from `web-push`.");
      }
      return null;
    }
  },

  /**
   * Setup notifications (Register + Auto-subscribe if permitted)
   */
  setupNotifications: async () => {
    console.group("Notification Setup");
    const registration = await NotificationService.registerServiceWorker();

    if (registration) {
      const permission = Notification.permission;
      console.log("Current Notification Permission:", permission);

      if (permission === "granted") {
        console.log("Permission already granted. Attempting automatic subscription...");
        await NotificationService.subscribeUser();
      } else if (permission === "default") {
        console.log("Permission not yet requested. Auto-subscription will wait for user interaction.");
      } else {
        console.warn("Notifications are blocked by the user.");
      }
    }
    console.groupEnd();
  },

  /**
   * Unsubscribe the user from push notifications
   */
  unsubscribeUser: async (userId) => {
    if (!NotificationService.isSupported()) return;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      try {
        await subscription.unsubscribe();
        console.log("User successfully unsubscribed from browser push.");

        // Notify backend
        const activeUserId = userId || cookieStorage.getItem("userId");
        if (activeUserId) {
          await ApiService.unsubscribePush({
            userId: activeUserId,
            endpoint: subscription.endpoint,
          });
          console.log("Backend notified of unsubscription.");
        }
      } catch (err) {
        console.error("Error during unsubscription:", err);
      }
    }
  },

  /**
   * Watch for browser permission changes
   */
  watchPermission: (callback) => {
    if (!("permissions" in navigator)) return null;

    navigator.permissions.query({ name: "notifications" }).then((status) => {
      status.onchange = () => {
        console.log("Browser notification permission changed:", status.state);
        callback(status.state);
      };
    });

    // Return the current state for immediate use
    return Notification.permission;
  },

  /**
   * Check if the user is currently subscribed
   */
  checkSubscription: async () => {
    if (!NotificationService.isSupported()) return false;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (err) {
      console.error("Error checking subscription status:", err);
      return false;
    }
  },
};
